import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { axiosInstance, getAiRequestErrorMessage } from "@/lib/api";
import { queue } from "@/components/Toast";
import { useAuthStore } from "@/stores/authStore";
import type {
  Reference,
  ReferenceCollection,
  ReferenceCollectionsResponse,
} from "@/types/reference.type";
import { useOrgId } from "./useReferences";
import {
  addReferences,
  cancelReferenceFetches,
  collectionsKey,
  invalidateCollectionWall,
  isCollectionWall,
  patchCollection,
  patchReference,
  readCollections,
  readReference,
  removeReferences,
} from "./referenceCache";

/**
 * Collections of references (backend: ReferenceCollectionUseCases). One read of
 * every collection the viewer can see; the page picks the brand's and Everyone's
 * out of it, so switching scope never waits on a second request.
 */

/** Same reasoning as the wall: covers are presigned, so the list never polls. */
const COLLECTIONS_STALE_MS = 5 * 60_000;

const TEMP_PREFIX = "temp-";
export const isTempCollection = (id: string) => id.startsWith(TEMP_PREFIX);

const toggleKey = (orgId: string | null) => ["reference-collection-items", orgId];

function failed(title: string, error: unknown) {
  queue.add({ title, description: getAiRequestErrorMessage(error, "Please try again."), variant: "error" });
}

export function useReferenceCollections(opts?: { enabled?: boolean }) {
  const orgId = useOrgId();
  const query = useQuery({
    queryKey: collectionsKey(orgId),
    queryFn: async () => {
      const res = await axiosInstance.get<ReferenceCollectionsResponse>(
        `/references/${orgId}/collections`,
      );
      return res.data.collections;
    },
    enabled: !!orgId && (opts?.enabled ?? true),
    staleTime: COLLECTIONS_STALE_MS,
  });
  return {
    collections: query.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
  };
}

export interface CreateCollectionInput {
  name: string;
  brandId: string | null;
  /** Filed into the new collection as soon as it exists (New collection from Collect). */
  references?: Reference[];
}

export function useCreateCollection() {
  const orgId = useOrgId();
  const qc = useQueryClient();
  const user = useAuthStore((s) => s.user);

  return useMutation({
    mutationFn: async ({ name, brandId, references }: CreateCollectionInput) => {
      const res = await axiosInstance.post<ReferenceCollection>(`/references/${orgId}/collections`, {
        name,
        brandId,
      });
      if (!references?.length) return res.data;
      const added = await axiosInstance.post<{ collection: ReferenceCollection }>(
        `/references/${orgId}/collections/${res.data.id}/references`,
        { referenceIds: references.map((r) => r.id) },
      );
      return added.data.collection;
    },
    onMutate: async ({ name, brandId, references = [] }) => {
      await qc.cancelQueries({ queryKey: collectionsKey(orgId) });
      await cancelReferenceFetches(qc, orgId);
      const tempId = `${TEMP_PREFIX}${crypto.randomUUID()}`;
      const now = new Date().toISOString();
      const draft: ReferenceCollection = {
        id: tempId,
        name: name.trim(),
        brandId,
        count: references.length,
        cover: references
          .filter((r) => r.imageUrl || r.videoUrl)
          .slice(0, 3)
          .map((r) => ({ referenceId: r.id, imageUrl: r.imageUrl, videoUrl: r.videoUrl })),
        createdBy: { id: user?.id ?? "", name: user?.name ?? null, role: null },
        createdAt: now,
        updatedAt: now,
      };
      qc.setQueryData<ReferenceCollection[]>(collectionsKey(orgId), (list) => [draft, ...(list ?? [])]);
      for (const r of references) {
        patchReference(qc, orgId, r.id, (ref) => ({
          ...ref,
          collectionIds: [...(ref.collectionIds ?? []), tempId],
        }));
      }
      return { tempId, referenceIds: references.map((r) => r.id) };
    },
    onSuccess: (collection, _input, context) => {
      qc.setQueryData<ReferenceCollection[]>(collectionsKey(orgId), (list) =>
        list?.map((c) => (c.id === context?.tempId ? collection : c)),
      );
      for (const id of context?.referenceIds ?? []) {
        patchReference(qc, orgId, id, (ref) => ({
          ...ref,
          collectionIds: (ref.collectionIds ?? []).map((c) => (c === context?.tempId ? collection.id : c)),
        }));
      }
    },
    onError: (error, _input, context) => {
      qc.setQueryData<ReferenceCollection[]>(collectionsKey(orgId), (list) =>
        list?.filter((c) => c.id !== context?.tempId),
      );
      for (const id of context?.referenceIds ?? []) {
        patchReference(qc, orgId, id, (ref) => ({
          ...ref,
          collectionIds: (ref.collectionIds ?? []).filter((c) => c !== context?.tempId),
        }));
      }
      failed("Could not create the collection", error);
    },
    onSettled: (collection) => {
      void qc.invalidateQueries({ queryKey: collectionsKey(orgId) });
      if (collection) void invalidateCollectionWall(qc, orgId, collection.id);
    },
  });
}

export function useRenameCollection() {
  const orgId = useOrgId();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({ collectionId, name }: { collectionId: string; name: string }) => {
      const res = await axiosInstance.patch<ReferenceCollection>(
        `/references/${orgId}/collections/${collectionId}`,
        { name },
      );
      return res.data;
    },
    onMutate: async ({ collectionId, name }) => {
      await qc.cancelQueries({ queryKey: collectionsKey(orgId) });
      const before = readCollections(qc, orgId)?.find((c) => c.id === collectionId);
      patchCollection(qc, orgId, collectionId, (c) => ({ ...c, name: name.trim() }));
      return { before };
    },
    onError: (error, { collectionId }, context) => {
      if (context?.before) patchCollection(qc, orgId, collectionId, () => context.before!);
      failed("Could not rename the collection", error);
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: collectionsKey(orgId) });
    },
  });
}

export function useDeleteCollection() {
  const orgId = useOrgId();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (collectionId: string) => {
      await axiosInstance.delete(`/references/${orgId}/collections/${collectionId}`);
    },
    onMutate: async (collectionId) => {
      await qc.cancelQueries({ queryKey: collectionsKey(orgId) });
      const list = readCollections(qc, orgId) ?? [];
      const index = list.findIndex((c) => c.id === collectionId);
      qc.setQueryData<ReferenceCollection[]>(collectionsKey(orgId), list.filter((c) => c.id !== collectionId));
      return { removed: index >= 0 ? { collection: list[index], index } : null };
    },
    onError: (error, _id, context) => {
      const removed = context?.removed;
      if (removed) {
        qc.setQueryData<ReferenceCollection[]>(collectionsKey(orgId), (list = []) => {
          const next = [...list];
          next.splice(removed.index, 0, removed.collection);
          return next;
        });
      }
      failed("Could not delete the collection", error);
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: collectionsKey(orgId) });
      void qc.invalidateQueries({ queryKey: ["references", orgId] });
    },
  });
}

export interface ToggleCollectionInput {
  collectionId: string;
  references: Reference[];
  /** true files them in, false takes them out. */
  add: boolean;
}

/**
 * Tick or untick a collection for some references. Acts at once (the picker has
 * no Save): the ticks, the count and the cover move before the request lands,
 * and a refusal puts back only the rows this call touched.
 */
export function useToggleCollection() {
  const orgId = useOrgId();
  const qc = useQueryClient();

  return useMutation({
    mutationKey: toggleKey(orgId),
    mutationFn: async ({ collectionId, references, add }: ToggleCollectionInput) => {
      const path = add ? "references" : "references/remove";
      await axiosInstance.post(`/references/${orgId}/collections/${collectionId}/${path}`, {
        referenceIds: references.map((r) => r.id),
      });
    },
    onMutate: async ({ collectionId, references, add }) => {
      await cancelReferenceFetches(qc, orgId);
      await qc.cancelQueries({ queryKey: collectionsKey(orgId) });
      const before = references.map((r) => readReference(qc, orgId, r.id) ?? r);
      const collectionBefore = readCollections(qc, orgId)?.find((c) => c.id === collectionId);
      const changed = before.filter((r) => (r.collectionIds ?? []).includes(collectionId) !== add);

      for (const r of changed) {
        patchReference(qc, orgId, r.id, (ref) => ({
          ...ref,
          collectionIds: add
            ? [...(ref.collectionIds ?? []), collectionId]
            : (ref.collectionIds ?? []).filter((c) => c !== collectionId),
        }));
      }
      const ids = new Set(changed.map((r) => r.id));
      if (add) {
        addReferences(
          qc,
          orgId,
          collectionId,
          changed.map((r) => ({
            ...r,
            collectionIds: [...(r.collectionIds ?? []), collectionId],
          })),
        );
      }
      patchCollection(qc, orgId, collectionId, (c) => ({
        ...c,
        count: Math.max(0, c.count + (add ? changed.length : -changed.length)),
        cover: add
          ? [
              ...changed
                .filter((r) => r.imageUrl || r.videoUrl)
                .map((r) => ({ referenceId: r.id, imageUrl: r.imageUrl, videoUrl: r.videoUrl })),
              ...c.cover,
            ].slice(0, 3)
          : c.cover.filter((item) => !ids.has(item.referenceId)),
      }));
      // Taken out of the collection that is open: it leaves that wall at once.
      if (!add) removeReferences(qc, orgId, [...ids], (key) => isCollectionWall(key, collectionId));

      return { before: before.filter((r) => ids.has(r.id)), collectionBefore };
    },
    onError: (error, { collectionId }, context) => {
      for (const r of context?.before ?? []) patchReference(qc, orgId, r.id, () => r);
      if (context?.collectionBefore) patchCollection(qc, orgId, collectionId, () => context.collectionBefore!);
      failed("Could not update the collection", error);
      void qc.invalidateQueries({ queryKey: ["references", orgId] });
    },
    onSettled: (_data, _error, { collectionId }) => {
      if (qc.isMutating({ mutationKey: toggleKey(orgId) }) > 1) return;
      void qc.invalidateQueries({ queryKey: collectionsKey(orgId) });
      void invalidateCollectionWall(qc, orgId, collectionId);
    },
  });
}
